'use client';
import React from 'react';
import { useUser, useUserSubscriptions, useDetectIncognitoMode } from '@benzinga/user-context';
import { AuthenticationManager } from '@benzinga/session';
import { TrackingManager } from '@benzinga/tracking-manager';
import { runIdentify } from '@benzinga/analytics';
import { PageProps } from '../Page';
import { Session } from '@benzinga/session';

interface Props {
  pageProps: Omit<PageProps, 'children'>;
  session: Session;
}

export const GlobalTracking: React.FC<Props> = ({ pageProps, session }) => {
  const user = useUser();
  const subscriptions = useUserSubscriptions();
  const isIncognito = useDetectIncognitoMode();

  React.useEffect(() => {
    session?.getManager(TrackingManager).setUser(user);
  }, [session, user]);

  React.useEffect(() => {
    session?.getManager(TrackingManager).setSubscriptions(subscriptions);
  }, [session, subscriptions]);

  React.useEffect(() => {
    session?.getManager(TrackingManager).setMeta(pageProps?.metaProps || null);
  }, [session, pageProps?.metaProps]);

  React.useEffect(() => {
    session?.getManager(TrackingManager).updateContext({ isIncognito: isIncognito });
  }, [session, isIncognito]);

  const lastIdentifiedUserIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const authManager = session.getManager(AuthenticationManager);

    const runTracking = () => {
      const user = authManager.getAuthentication()?.user;
      session.getManager(TrackingManager).setUser(user ?? null);
      if (user && user.accessType !== 'anonymous') {
        if (lastIdentifiedUserIdRef.current !== user.benzingaUid) {
          lastIdentifiedUserIdRef.current = user.benzingaUid;
          runIdentify(session, user);
        }
      } else {
        lastIdentifiedUserIdRef.current = null;
      }
    };

    const authentication = authManager.getAuthentication();

    if (authentication) {
      runTracking();
      return;
    }

    const subscription = authManager.subscribe(
      () => {
        runTracking();
        subscription.unsubscribe();
      },
      undefined,
      ['authentication:session_update'],
    );

    return () => subscription.unsubscribe();
  }, [session]);

  return null;
};
